import { useState } from 'react';
import { X } from 'lucide-react';
import { Button, Input, Spinner } from '@/components/ui';
import { Modal } from '@/components/ui/Modal';
import { clinicsService } from '@/services/api/clinics.service';
import { Clinic } from '@/types/models';
import toast from 'react-hot-toast';

interface CreateClinicModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (clinic: Clinic) => void;
}

export function CreateClinicModal({ isOpen, onClose, onSuccess }: CreateClinicModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    cnpj: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome da clínica é obrigatório';
    }

    if (formData.phone && !/^(\(\d{2}\)\s?)?(\d{4,5}-?\d{4})$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Telefone inválido. Use o formato: (00) 00000-0000';
    }

    if (formData.cnpj && !/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(formData.cnpj)) {
      newErrors.cnpj = 'CNPJ inválido. Use o formato: 00.000.000/0000-00';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const newClinic = await clinicsService.create({
        ...formData,
        isActive: true
      });

      onSuccess(newClinic);
      handleClose();
    } catch (error: any) {
      console.error('Error creating clinic:', error);
      toast.error(error.response?.data?.error || 'Erro ao criar clínica');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      address: '',
      phone: '',
      cnpj: ''
    });
    setErrors({});
    onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpar erro do campo ao digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-md w-full">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-900">
              Criar Nova Clínica
            </h2>
            <button
              onClick={handleClose}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
              disabled={isSubmitting}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-4">
              <Input
                label="Nome da Clínica"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={errors.name}
                placeholder="Ex: Clínica Centro"
                required
              />

              <Input
                label="Endereço"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                error={errors.address}
                placeholder="Rua, número, bairro, cidade"
              />

              <Input
                label="Telefone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={errors.phone}
                placeholder="(00) 00000-0000"
              />

              <Input
                label="CNPJ"
                value={formData.cnpj}
                onChange={(e) => handleInputChange('cnpj', e.target.value)}
                error={errors.cnpj}
                placeholder="00.000.000/0000-00"
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 rounded-b-lg">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Criando...
                  </>
                ) : (
                  'Criar Clínica'
                )}
              </Button>
            </div>
          </form>
    </Modal>
  );
}